import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import '../../css/modals.css';

const emptyValues = () => ({ name: '', description: '', level: '', typeId: '' });

// The plain, non-AI "+" on /conversations-menu - just enough to create the
// row (name/description/level/type) and land it in the template list; every
// other field (estimation function, pipeline stage, questions...) is
// already editable from inside the canvas constructor itself
// (ConversationInfoPanel/EstimationFunctionButton/PipelineButton), so there
// is no need to ask for them twice. AI generation is a deliberately
// separate, dedicated flow (/conversations-ai-generator, its own "+AI"
// button) - this modal never calls it.
export default function ConversationCreateModal({ isOpen, onClose, onCreated }) {
   const navigate = useNavigate();
   const lang = language[language.lang];

   const [values, setValues] = useState(emptyValues());
   const [levels, setLevels] = useState([]);
   const [types, setTypes] = useState([]);
   const [saving, setSaving] = useState(false);

   useEffect(() => {
      if(!isOpen) return;
      axios.get('/api/levels', { withCredentials: true })
         .then(r => setLevels(Array.isArray(r.data) ? r.data : []))
         .catch(() => setLevels([]));
      axios.get('/api/conversationtypes', { withCredentials: true })
         .then(r => setTypes(Array.isArray(r.data) ? r.data : []))
         .catch(() => setTypes([]));
   }, [isOpen]);

   if(!isOpen) return '';

   const stopProp = e => e.stopPropagation();

   const handleFieldChange = (name, value) => {
      setValues(prev => ({ ...prev, [name]: value }));
   }

   const handleClose = () => {
      setValues(emptyValues());
      onClose();
   }

   const handleSubmit = (e) => {
      e.preventDefault();
      if(saving || !values.name.trim()) return;
      setSaving(true);
      // isPresent is NOT NULL in the DB (conversation.is_present) despite
      // being a nullable FK in the Java entity - omitting it 500s
      // (ConstraintViolationException, confirmed live). Not worth a field
      // in this deliberately minimal form (a teacher creating a template
      // has no reason to think about it up front), so it's always sent as
      // the "No" boolean row (id 2, confirmed against the `boolean` table) -
      // same default the old /conversations create form's own isPresent
      // selector effectively landed on when left untouched.
      const payload = { name: values.name.trim(), isPresent: '2' };
      if(values.description.trim()) payload.description = values.description.trim();
      if(values.level) payload.level = values.level;
      if(values.typeId) payload.typeId = values.typeId;
      axios.post('/api/conversations', JSON.stringify(payload), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setValues(emptyValues());
         onCreated();
      }).catch(err => {
         if(err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setSaving(false));
   }

   return (
      <div className='modal-update' onClick={handleClose} style={{ display: 'flex' }}>
         <div className='modal-window' onClick={stopProp}>
            <h3 className='modal-w-title'>{lang.modals.conversations.createTitle}</h3>
            <form onSubmit={handleSubmit}>
               <div className='student-lesson-create-fields'>
                  <div className='flex-from-input-wraper student-lesson-create-field-full' data-label={lang.name}>
                     <input className='submit-field from-input txt-c' type='text' required
                        value={values.name} onChange={(e) => handleFieldChange('name', e.target.value)} />
                  </div>
                  <div className='flex-from-input-wraper student-lesson-create-field-full' data-label={lang.description}>
                     <input className='submit-field from-input txt-c' type='text'
                        value={values.description} onChange={(e) => handleFieldChange('description', e.target.value)} />
                  </div>
                  <div className='flex-from-input-wraper student-lesson-create-field' data-label={lang.level}>
                     <select className='submit-field form-selection txt-c' value={values.level}
                        onChange={(e) => handleFieldChange('level', e.target.value)}>
                        <option value=''></option>
                        {levels.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                     </select>
                  </div>
                  <div className='flex-from-input-wraper student-lesson-create-field' data-label={lang.type}>
                     <select className='submit-field form-selection txt-c' value={values.typeId}
                        onChange={(e) => handleFieldChange('typeId', e.target.value)}>
                        <option value=''></option>
                        {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                     </select>
                  </div>
               </div>
               <button type='submit' className='form-button txt-c' disabled={saving || !values.name.trim()} style={{marginTop:14}}>
                  {saving ? lang['Loading ...'] : lang.save}
               </button>
            </form>
         </div>
      </div>
   );
}
